import { Lambda } from 'aws-sdk';
import CognitoUtil from '../cognito/cognitoUtil';
import awsAppConfig from '../../aws/cognito/config.js';
import moment from 'moment';
import { GraphQlError } from './errors';

const REPORT_UNITS_METRIC = 'Metric';
const REPORT_UNITS_IMPERIAL = 'Imperial';
const VALID_REPORT_UNITS = [REPORT_UNITS_METRIC, REPORT_UNITS_IMPERIAL];

export const temporary_unitOfMeasureToReportUnits = unitOfMeasure => {
    if(unitOfMeasure === 'METRIC') return REPORT_UNITS_METRIC;
    return REPORT_UNITS_IMPERIAL;
}

const assertValidUnitOfMeasure = unitOfMeasure => {
    if (!VALID_REPORT_UNITS.includes(unitOfMeasure)) throw new Error(`Invalid unitOfMeasure provided: ${unitOfMeasure}`);
}

const deflate = query => query.replace(/\s{2,}/g, ' ');

const maybeAddQualifierToParams = (stage, params) => {
    // if (['dev'].includes(stage)) {
    //     params.Qualifier = stage;
    //     console.log('adding qualifier', params.FunctionName);
    // }
    return params;
}

export default class LambdaUtil {

    static createLambdaClient = () => {
        window.lambda = new Lambda({
            region: awsAppConfig.region,
            maxRetries: 1,
            httpOptions: {
                timeout: 300000
            }
        });
    }

    static invokeLambda = (params, onSuccess, onError) => {

        // franchise view: get current account and add to context
        const userCurrentAccount = CognitoUtil.getUserAccountsDetails();
        if(userCurrentAccount){
            const customContextItem = `{"custom":{"accountExternalId":"${userCurrentAccount.account.accountId}"}}`;
            const base64CustomContextItem = btoa(customContextItem)  // equivalent to python version base64.b64encode(b'<string>)
            //console.log('base64CustomContextItem', customContextItem, base64CustomContextItem , userCurrentAccount);
            params.ClientContext = base64CustomContextItem;
        }

        // console.log("Invoking Lambda", params);
        if (!window.lambda) {
            LambdaUtil.createLambdaClient();
        }
        let refreshAttempted = false;

        window.lambda.invoke(params, (err, data) => {
            // if (!refreshAttempted) err = 'testerr'; //DEBUG
            if (err) {
                if (err.code === 'ResourceNotFoundException') {
                    return onError && onError(err);
                }
                // console.log("ERROR:", err);
                //attempt to refresh login and re-attempt before failing for good and kicking out to login screen.
                //maintains scope of params, err, data, onSuccess, onError
                if (!refreshAttempted) {
                    console.warn('Lambda Failed, CognitoUtil attempting refresh');
                    refreshAttempted = true;
                    CognitoUtil.refreshLogin(() => {
                        // Reset the lambda client prior to the new attempt
                        LambdaUtil.createLambdaClient();
                        window.lambda.invoke(params, (err2, data2) => {
                            if (err2) {
                                if (onError) onError(err2);
                            } else {
                                if (onSuccess) onSuccess(data2);
                            }
                        })
                    }, (err3) => {
                        if (onError) onError(err3);
                    });
                } else {
                    console.error(err);
                    if (onError) onError(err);
                }
            } else {
                if (onSuccess) onSuccess(data);
            };
        });
    }

    static invokeLambdaForPromise = (params, ignorePayload) => {
        return new Promise(function(resolve, reject) {
            LambdaUtil.invokeLambda(params,
                /* success */ (response) => {
                    if(response.StatusCode !== 200 ) {
                        reject(Error(`StatusCode: ${response.StatusCode}`));
                        return;
                    }
                    if (ignorePayload) {
                        resolve();
                        return;
                    }
                    try {
                        let payload = JSON.parse(response.Payload);
                        if(payload.status === "ERROR") {
                            // console.log('lambda responded with ERROR status', params, payload);
                            reject(Error(`${params.FunctionName} responded with ERROR: '${payload.message}'`));
                            return;
                        }
                        resolve(payload);
                    } catch (e) {
                        reject(e);
                        return;
                    }
                },
                /* failure */ (err) => {
                    reject(err instanceof Error ? err : Error(err));
                }
            );
        });
    }

    static invokeGraphQL = (stage, payload, onSuccess, onError) => {

        var params = {
            FunctionName: "lambda-kloud-" + stage + "-queryGraphQL",
            InvocationType: "RequestResponse",
            LogType: "Tail",
            Payload: payload
        };
        LambdaUtil.invokeLambda(maybeAddQualifierToParams(stage, params), onSuccess, onError);
    }

    static invokeGraphQLForPromise = (stage, payload) => {
        return new Promise(function(resolve, reject) {
            LambdaUtil.invokeGraphQL(stage, payload,
                /* success */ (response) => {
                    if(response.StatusCode !== 200 ) {
                        reject(Error(`StatusCode: ${response.StatusCode}`));
                        return;
                    }
                    try {
                        let payload = JSON.parse(response.Payload);
                        resolve(payload);
                    } catch (e) {
                        reject(e);
                        return;
                    }
                },
                /* failure */ (err) => {
                    reject(err instanceof Error ? err : Error(err));
                }
            );
        });
    }

    static invokeTripShare = (stage, payload, onSuccess, onError) => {

        var params = {
            FunctionName: "lambda-kloud-" + stage + "-tripSharing",
            InvocationType: "RequestResponse",
            LogType: "Tail",
            Payload: payload,
        };
        LambdaUtil.invokeLambda(params, onSuccess, onError);
    }

    static invokeRavenStats = (stage, payload, onSuccess, onError) => {

        var params = {
            FunctionName: "lambda-kloud-" + stage + "-queryRavenStats",
            InvocationType: "RequestResponse",
            LogType: "Tail",
            Payload: payload,
        };
        LambdaUtil.invokeLambda(params, onSuccess, onError);
    }

    //create Kloud user associated with cognito user
    static createKloudUser(stage, email, driverName, onSuccess, onError) {

        var payload = '{ "email": "' + email + '", "driverName": "' + driverName + '" }';
        var params = {
            FunctionName: "lambda-kloud-" + stage + "-createUser",
            InvocationType: "RequestResponse",
            LogType: "Tail",
            Payload: payload,
        };
        // LambdaUtil.invokeLambda(params, LambdaUtil.createKloudAccount(onSuccess, onError), onError);//create account for testing billing information - should never actually be done from web app.
        LambdaUtil.invokeLambda(params, onSuccess, onError);
    }

    static createChannelKloudAccount(stage, channelCode, customFields, onSuccess, onError) {
        var payload = JSON.stringify({
            channelCode: channelCode,
            customFields: customFields
        });
        var params = {
            FunctionName: "lambda-kloud-" + stage + "-createAccount",
            InvocationType: "RequestResponse",
            LogType: "Tail",
            Payload: payload
        };
        LambdaUtil.invokeLambda(params, onSuccess, onError);
    }

    //create recurly account associated with Kloud user
    static createKloudAccount(stage, onSuccess, onError) {

        var params = {
            FunctionName: "lambda-kloud-" + stage + "-createAccount",
            InvocationType: "RequestResponse",
            LogType: "Tail",
        };
        LambdaUtil.invokeLambda(params, onSuccess, onError);
    }

    static generateVehicleQueryString(ravenUuid)
    {
        return deflate(`
            vehicle_${ravenUuid}: vehicleState (ravenUuid:"${ravenUuid}") {
                segId
                currentSpeed
                currentCompassHeading
                currentFuel
                currentPreviewUrls { ts type url cameraType }
                ravenOffline
        }`);

    }

    static generateDashboardQueryString(ravenUuid)
    {
        return deflate(`
            raven_${ravenUuid}: dashboard (ravenUuid:"${ravenUuid}") {
                ravenId
                cardId
                type
                thumbnailUrl
                start { lat lon ts }
                end { lat lon ts }
                route { lat lon ts }
                segments {
                    segId
                    type
                    speedZones {
                        maxSpeed
                        speedLimit
                        startTime
                        endTime
                    }
                }
                drivers {
                    name
                    startTime
                    image
                    identityId
                }
                hasPassengers
                media {
                    mediaId
                    ts
                    type
                    cameraType
                    url
                    status
                    statusTime
                    availableOnRaven
                }
                eventList {
                    type
                    ts
                    duration
                    description
                    videoAvailable {
                        cabin
                        road
                    }
                    mediaList {
                        mediaId
                    }
                    geoStamp { ts lon lat }
                }
                events {
                    type
                    ts
                    duration
                    description
                    videoAvailable {
                        cabin
                        road
                    }
                    level
                    media {
                        mediaId
                        ts
                        type
                        cameraType
                        url
                        availableOnRaven
                    }
                    mediaAutomatch
                    geoStamp { ts lon lat }
                    eventParams { key value }
                }
            }
        `);
    }


    static getVehicleState(stage, ravenUuid, onSuccess, onError) {

        // console.log("query vehicle state");
        const query = deflate(`query {
            vehicleState (ravenUuid:"${ravenUuid}") {
                segId
                currentSpeed
                currentCompassHeading
                currentFuel
                currentPreviewUrls { ts type url cameraType }
                ravenOffline
            }
        }`);

        if (!onSuccess) {
            return this.invokeGraphQLForPromise(stage, JSON.stringify({ query }));
        }

        this.invokeGraphQL(stage, JSON.stringify({ query }), onSuccess, onError);
    }

    // Get the vehicle state for all listed RavenUuids and the dashboard for the selected one.
    // If the selectd RavenUuid is not in the list of all RavenUuids, it won't get the vehicle state
    static getCurrentVehicleStates(stage, ravenUuids, selectedRavenUuid, onSuccess, onError) {

        var query = 'query {';

        for(var i = 0 ; i < ravenUuids.length; i++) {

            const ravenUuid = ravenUuids[i];

            query += this.generateVehicleQueryString(ravenUuid);

            if(ravenUuid === selectedRavenUuid)
            {
                query += this.generateDashboardQueryString(selectedRavenUuid);
            }

        }

        query += '}';

        if (!onSuccess) {
            return this.invokeGraphQLForPromise(stage, JSON.stringify({ query }));
        }

        this.invokeGraphQL(stage, JSON.stringify({ query }), onSuccess, onError);
    }

    static getRavenDashboardAndVehicleStates(stage, ravenUuids, onSuccess, onError) {

        // TODO: only include mediaId under events.media

        var query = 'query {';

        for(var i = 0 ; i < ravenUuids.length; i++) {

            const ravenUuid = ravenUuids[i];

            query += this.generateDashboardQueryString(ravenUuid);
            query += this.generateVehicleQueryString(ravenUuid);
        }

        query += '}';

        if (!onSuccess) {
            return this.invokeGraphQLForPromise(stage, JSON.stringify({ query }));
        }

        this.invokeGraphQL(stage, JSON.stringify({ query }), onSuccess, onError);

    }


    static getRavens(stage, onSuccess, onError) {
        // console.log("invoke ravens ", stage);
        const query = deflate(`query {
            ravens {
                ravens {
                    ravenUuid
                    stage
                    status
                    features {
                        name
                        supported
                    }
                    vehicle {
                        name
                        fuelLevelSupported
                        currentDriver  {
                            name
                            image
                            identityId
                        }
                        hasPassengers
                        activeDTCs {
                            code
                            title
                            possibleCauses
                            severity
                            ts
                        }
                    }
                    ravenUsers { email driverName accessLevel }
                    accessLevel
                    permissions
                    unplugged
                    lastLocation { lat lon ts }
                    ravenApiLevel
                    view
                    sdCardStatus
                }
            }
        }`);

        if (!onSuccess) {
            return this.invokeGraphQLForPromise(stage, JSON.stringify({ query }));
        }

        this.invokeGraphQL(stage, JSON.stringify({ query }), onSuccess, onError);
    }

    static getTripShareUrl(stage, ravenUuid, destination, onSuccess, onError) {

        // console.log("invoke trip share");
        var payload = '{"ravenUuid":"' + ravenUuid + '" }';
        if (destination) {
            payload = '{"ravenUuid":"' + ravenUuid + '", "destination": { "lat":' + destination[1] + ', "lon":' + destination[0] + '} }';
        }
        this.invokeTripShare(stage, payload, onSuccess, onError);
    }

    static getAccountDetails(stage, permissionsToRequest, onSuccess, onError) {

        var params = {
            FunctionName: "lambda-kloud-" + stage + "-lookupAccount",
            InvocationType: "RequestResponse",
            LogType: "Tail"
        };

        if(Array.isArray(permissionsToRequest) && permissionsToRequest.length > 0) {
            params.Payload = JSON.stringify({
                requestedPermissions: [...permissionsToRequest]
            });
        }

        LambdaUtil.invokeLambda(maybeAddQualifierToParams(stage, params), onSuccess, onError);
    }

    static getAccounts(stage, onSuccess, onError) {
        const query = deflate(`query { 
            accounts { 
                accounts { 
                    accountId 
                    role 
                    name 
                    email
                    ravenCount
                } 
            } 
        }`);

        this.invokeGraphQL(stage, JSON.stringify({ query }), onSuccess, onError);
    }

    static listServicePlans(stage, onSuccess, onError) {

        // console.log("list service plans ", stage);
        var payload = '{"query": "query {listServicePlans { plans { planCode } }}" }';
        this.invokeGraphQL(stage, payload, onSuccess, onError);
    }

    static updateAccountBillingInformation(stage, tokenId, onSuccess, onError) {

        var payload = '{"billingTokenId": "' + tokenId + '"}';
        var params = {
            FunctionName: "lambda-kloud-" + stage + "-updateBillingInfo",
            InvocationType: "RequestResponse",
            LogType: "Tail",
            Payload: payload
        };
        LambdaUtil.invokeLambda(params, onSuccess, onError);
    }

    //only to be used when setting up a new raven
    static subscribeServicePlan(stage, planCode, ravenUuid, onSuccess, onError) {

        var payload = '{"planCode": "' + planCode + '", "ravenUuid":"' + ravenUuid + '"}';
        var params = {
            FunctionName: "lambda-kloud-" + stage + "-subscribeServicePlan",
            InvocationType: "RequestResponse",
            LogType: "Tail",
            Payload: payload
        };
        LambdaUtil.invokeLambda(params, onSuccess, onError);
    }

    //updateserviceplan
    //subscriptionuuid, plancode
    static updateServicePlan(stage, planCode, subscriptionUuid, onSuccess, onError) {

        var payload = '{"planCode": "' + planCode + '", "subscriptionUuid":"' + subscriptionUuid + '"}';
        var params = {
            FunctionName: "lambda-kloud-" + stage + "-updateServicePlan",
            InvocationType: "RequestResponse",
            LogType: "Tail",
            Payload: payload
        };
        LambdaUtil.invokeLambda(params, onSuccess, onError);
    }

    static cancelServicePlan(stage, subscriptionUuid, onSuccess, onError) {

        var payload = '{"subscriptionUuid":"' + subscriptionUuid + '"}';
        var params = {
            FunctionName: "lambda-kloud-" + stage + "-cancelServicePlan",
            InvocationType: "RequestResponse",
            LogType: "Tail",
            Payload: payload
        };
        LambdaUtil.invokeLambda(params, onSuccess, onError);
    }

    static createOrder(stage, orderDetails, onSuccess, onError) {
        /*
        Sample request:
        POST /accounts/{account_uuid}/orders
        {
            "orderItems": [    {
                "variantId": "gid://shopify/ProductVariant/7979752849584",
                "quantity": 1
                },
                {
                "variantId": "gid://shopify/ProductVariant/6952256569520",
                "quantity": 1
                },
                {
                "variantId": "gid://shopify/ProductVariant/1635112779850",
                "quantity": 1
                },
                {
                "variantId": "gid://shopify/ProductVariant/6590161518768",
                "quantity": 1
                }
            ],
            "shippingInfo": {
                "firstName": "Robert",
                "lastName": "Watson",
                "address1": "441 Maclaren Street",
                "address2": "400",
                "city": "Ottawa",
                "state": "Ontario",
                "zip": "123456",
                "phone": "16136136113"
            },
            "billingInfo": {
                "firstName": "Robert",
                "lastName": "Watson",
                "address1": "441 Maclaren Street",
                "address2": "400",
                "city": "Ottawa",
                "state": "Ontario",
                "zip": "a1a1a1",
                "country": "Canada",
                "recurlyTokenId": "qLsCkhMXTZsj4sxuRVagTQ"
            }
        }

        */
        var payload = JSON.stringify(orderDetails);
        console.log("create order payload:");
        console.log(payload);
    }

    static listInvoices(stage, fromDate, toDate, onSuccess, onError) {

        fromDate = moment(fromDate).format("YYYY-MM-DD");
        toDate = moment(toDate).format("YYYY-MM-DD");

        var payload = '{"fromDate": "' + fromDate + '", "toDate": "' + toDate + '"}';

        var params = {
            FunctionName: "lambda-kloud-" + stage + "-listInvoices",
            InvocationType: "RequestResponse",
            LogType: "Tail",
            Payload: payload
        };
        //test success 
        // var response = {};
        // response.Payload = { "status": "SUCCESS", "payload": [{ "invoiceNumber": 10585, "amountInCents": 0, "invoiceDate": "2018-08-08" }, { "invoiceNumber": 10489, "amountInCents": 0, "invoiceDate": "2018-08-07" }, { "invoiceNumber": 10488, "amountInCents": 0, "invoiceDate": "2018-08-07" }, { "invoiceNumber": 10455, "amountInCents": 0, "invoiceDate": "2018-08-07" }, { "invoiceNumber": 10420, "amountInCents": 0, "invoiceDate": "2018-08-07" }, { "invoiceNumber": 10419, "amountInCents": 0, "invoiceDate": "2018-08-07" }, { "invoiceNumber": 10284, "amountInCents": 737, "invoiceDate": "2018-08-06" }, { "invoiceNumber": 10268, "amountInCents": 671, "invoiceDate": "2018-08-06" }, { "invoiceNumber": 9981, "amountInCents": 52, "invoiceDate": "2018-08-04" }, { "invoiceNumber": 9893, "amountInCents": 0, "invoiceDate": "2018-08-03" }, { "invoiceNumber": 9798, "amountInCents": 0, "invoiceDate": "2018-08-02" }, { "invoiceNumber": 9667, "amountInCents": 0, "invoiceDate": "2018-08-01" }, { "invoiceNumber": 9610, "amountInCents": 0, "invoiceDate": "2018-08-01" }, { "invoiceNumber": 9609, "amountInCents": 0, "invoiceDate": "2018-08-01" }, { "invoiceNumber": 9608, "amountInCents": 800, "invoiceDate": "2018-08-01" }, { "invoiceNumber": 9607, "amountInCents": 0, "invoiceDate": "2018-08-01" }, { "invoiceNumber": 9596, "amountInCents": 800, "invoiceDate": "2018-08-01" }, { "invoiceNumber": 9557, "amountInCents": 0, "invoiceDate": "2018-07-31" }, { "invoiceNumber": 9532, "amountInCents": 0, "invoiceDate": "2018-07-31" }, { "invoiceNumber": 9426, "amountInCents": 0, "invoiceDate": "2018-07-30" }, { "invoiceNumber": 9401, "amountInCents": 0, "invoiceDate": "2018-07-30" }, { "invoiceNumber": 9298, "amountInCents": 0, "invoiceDate": "2018-07-29" }, { "invoiceNumber": 9273, "amountInCents": 0, "invoiceDate": "2018-07-29" }, { "invoiceNumber": 9171, "amountInCents": 0, "invoiceDate": "2018-07-28" }, { "invoiceNumber": 9146, "amountInCents": 0, "invoiceDate": "2018-07-28" }, { "invoiceNumber": 9044, "amountInCents": 0, "invoiceDate": "2018-07-27" }, { "invoiceNumber": 9019, "amountInCents": 0, "invoiceDate": "2018-07-27" }, { "invoiceNumber": 8917, "amountInCents": 0, "invoiceDate": "2018-07-26" }, { "invoiceNumber": 8892, "amountInCents": 0, "invoiceDate": "2018-07-26" }, { "invoiceNumber": 8790, "amountInCents": 0, "invoiceDate": "2018-07-25" }, { "invoiceNumber": 8765, "amountInCents": 0, "invoiceDate": "2018-07-25" }, { "invoiceNumber": 8663, "amountInCents": 0, "invoiceDate": "2018-07-24" }, { "invoiceNumber": 8638, "amountInCents": 0, "invoiceDate": "2018-07-24" }, { "invoiceNumber": 8536, "amountInCents": 0, "invoiceDate": "2018-07-23" }, { "invoiceNumber": 8511, "amountInCents": 0, "invoiceDate": "2018-07-23" }, { "invoiceNumber": 8409, "amountInCents": 0, "invoiceDate": "2018-07-22" }, { "invoiceNumber": 8384, "amountInCents": 0, "invoiceDate": "2018-07-22" }, { "invoiceNumber": 8282, "amountInCents": 0, "invoiceDate": "2018-07-21" }, { "invoiceNumber": 8257, "amountInCents": 0, "invoiceDate": "2018-07-21" }, { "invoiceNumber": 8155, "amountInCents": 0, "invoiceDate": "2018-07-20" }, { "invoiceNumber": 8130, "amountInCents": 0, "invoiceDate": "2018-07-20" }, { "invoiceNumber": 8028, "amountInCents": 0, "invoiceDate": "2018-07-19" }, { "invoiceNumber": 8003, "amountInCents": 0, "invoiceDate": "2018-07-19" }, { "invoiceNumber": 7901, "amountInCents": 0, "invoiceDate": "2018-07-18" }, { "invoiceNumber": 7876, "amountInCents": 0, "invoiceDate": "2018-07-18" }, { "invoiceNumber": 7774, "amountInCents": 0, "invoiceDate": "2018-07-17" }, { "invoiceNumber": 7749, "amountInCents": 0, "invoiceDate": "2018-07-17" }, { "invoiceNumber": 7647, "amountInCents": 0, "invoiceDate": "2018-07-16" }, { "invoiceNumber": 7622, "amountInCents": 0, "invoiceDate": "2018-07-16" }, { "invoiceNumber": 7520, "amountInCents": 0, "invoiceDate": "2018-07-15" }, { "invoiceNumber": 7495, "amountInCents": 0, "invoiceDate": "2018-07-15" }, { "invoiceNumber": 7393, "amountInCents": 0, "invoiceDate": "2018-07-14" }, { "invoiceNumber": 7368, "amountInCents": 0, "invoiceDate": "2018-07-14" }, { "invoiceNumber": 7266, "amountInCents": 0, "invoiceDate": "2018-07-13" }, { "invoiceNumber": 7241, "amountInCents": 0, "invoiceDate": "2018-07-13" }, { "invoiceNumber": 7139, "amountInCents": 0, "invoiceDate": "2018-07-12" }, { "invoiceNumber": 7113, "amountInCents": 0, "invoiceDate": "2018-07-12" }, { "invoiceNumber": 7015, "amountInCents": 0, "invoiceDate": "2018-07-11" }, { "invoiceNumber": 6991, "amountInCents": 0, "invoiceDate": "2018-07-11" }, { "invoiceNumber": 6895, "amountInCents": 0, "invoiceDate": "2018-07-10" }, { "invoiceNumber": 6871, "amountInCents": 0, "invoiceDate": "2018-07-10" }, { "invoiceNumber": 6775, "amountInCents": 0, "invoiceDate": "2018-07-09" }, { "invoiceNumber": 6751, "amountInCents": 0, "invoiceDate": "2018-07-09" }, { "invoiceNumber": 6655, "amountInCents": 0, "invoiceDate": "2018-07-08" }, { "invoiceNumber": 6632, "amountInCents": 0, "invoiceDate": "2018-07-08" }, { "invoiceNumber": 6537, "amountInCents": 0, "invoiceDate": "2018-07-07" }, { "invoiceNumber": 6514, "amountInCents": 0, "invoiceDate": "2018-07-07" }, { "invoiceNumber": 6419, "amountInCents": 0, "invoiceDate": "2018-07-06" }, { "invoiceNumber": 6396, "amountInCents": 0, "invoiceDate": "2018-07-06" }, { "invoiceNumber": 6300, "amountInCents": 0, "invoiceDate": "2018-07-05" }, { "invoiceNumber": 6276, "amountInCents": 0, "invoiceDate": "2018-07-05" }, { "invoiceNumber": 6182, "amountInCents": 0, "invoiceDate": "2018-07-04" }, { "invoiceNumber": 6159, "amountInCents": 0, "invoiceDate": "2018-07-04" }, { "invoiceNumber": 6065, "amountInCents": 0, "invoiceDate": "2018-07-03" }, { "invoiceNumber": 6042, "amountInCents": 0, "invoiceDate": "2018-07-03" }, { "invoiceNumber": 5948, "amountInCents": 0, "invoiceDate": "2018-07-02" }, { "invoiceNumber": 5925, "amountInCents": 0, "invoiceDate": "2018-07-02" }, { "invoiceNumber": 5832, "amountInCents": 0, "invoiceDate": "2018-07-01" }, { "invoiceNumber": 5809, "amountInCents": 0, "invoiceDate": "2018-07-01" }, { "invoiceNumber": 5716, "amountInCents": 0, "invoiceDate": "2018-06-30" }, { "invoiceNumber": 5693, "amountInCents": 0, "invoiceDate": "2018-06-30" }, { "invoiceNumber": 5600, "amountInCents": 0, "invoiceDate": "2018-06-29" }, { "invoiceNumber": 5577, "amountInCents": 0, "invoiceDate": "2018-06-29" }, { "invoiceNumber": 5485, "amountInCents": 0, "invoiceDate": "2018-06-28" }, { "invoiceNumber": 5463, "amountInCents": 0, "invoiceDate": "2018-06-28" }, { "invoiceNumber": 5372, "amountInCents": 0, "invoiceDate": "2018-06-27" }, { "invoiceNumber": 5350, "amountInCents": 0, "invoiceDate": "2018-06-27" }, { "invoiceNumber": 5260, "amountInCents": 0, "invoiceDate": "2018-06-26" }, { "invoiceNumber": 5238, "amountInCents": 0, "invoiceDate": "2018-06-26" }, { "invoiceNumber": 5148, "amountInCents": 0, "invoiceDate": "2018-06-25" }, { "invoiceNumber": 5127, "amountInCents": 0, "invoiceDate": "2018-06-25" }, { "invoiceNumber": 5037, "amountInCents": 0, "invoiceDate": "2018-06-24" }, { "invoiceNumber": 5017, "amountInCents": 0, "invoiceDate": "2018-06-24" }, { "invoiceNumber": 4928, "amountInCents": 0, "invoiceDate": "2018-06-23" }, { "invoiceNumber": 4908, "amountInCents": 0, "invoiceDate": "2018-06-23" }, { "invoiceNumber": 4819, "amountInCents": 0, "invoiceDate": "2018-06-22" }, { "invoiceNumber": 4799, "amountInCents": 0, "invoiceDate": "2018-06-22" }, { "invoiceNumber": 4711, "amountInCents": 0, "invoiceDate": "2018-06-21" }, { "invoiceNumber": 4691, "amountInCents": 0, "invoiceDate": "2018-06-21" }, { "invoiceNumber": 4604, "amountInCents": 0, "invoiceDate": "2018-06-20" }, { "invoiceNumber": 4585, "amountInCents": 0, "invoiceDate": "2018-06-20" }, { "invoiceNumber": 4503, "amountInCents": 0, "invoiceDate": "2018-06-19" }, { "invoiceNumber": 4485, "amountInCents": 0, "invoiceDate": "2018-06-19" }, { "invoiceNumber": 4404, "amountInCents": 0, "invoiceDate": "2018-06-18" }, { "invoiceNumber": 4386, "amountInCents": 0, "invoiceDate": "2018-06-18" }, { "invoiceNumber": 4305, "amountInCents": 0, "invoiceDate": "2018-06-17" }, { "invoiceNumber": 4286, "amountInCents": 0, "invoiceDate": "2018-06-17" }, { "invoiceNumber": 4206, "amountInCents": 0, "invoiceDate": "2018-06-16" }, { "invoiceNumber": 4187, "amountInCents": 0, "invoiceDate": "2018-06-16" }, { "invoiceNumber": 4108, "amountInCents": 0, "invoiceDate": "2018-06-15" }, { "invoiceNumber": 4089, "amountInCents": 0, "invoiceDate": "2018-06-15" }, { "invoiceNumber": 4005, "amountInCents": 0, "invoiceDate": "2018-06-14" }, { "invoiceNumber": 3988, "amountInCents": 0, "invoiceDate": "2018-06-14" }, { "invoiceNumber": 3911, "amountInCents": 0, "invoiceDate": "2018-06-13" }, { "invoiceNumber": 3894, "amountInCents": 0, "invoiceDate": "2018-06-13" }, { "invoiceNumber": 3817, "amountInCents": 0, "invoiceDate": "2018-06-12" }, { "invoiceNumber": 3727, "amountInCents": 0, "invoiceDate": "2018-06-11" }, { "invoiceNumber": 3637, "amountInCents": 0, "invoiceDate": "2018-06-10" }, { "invoiceNumber": 3548, "amountInCents": 0, "invoiceDate": "2018-06-09" }, { "invoiceNumber": 3462, "amountInCents": 0, "invoiceDate": "2018-06-08" }, { "invoiceNumber": 3379, "amountInCents": 0, "invoiceDate": "2018-06-07" }, { "invoiceNumber": 3297, "amountInCents": 0, "invoiceDate": "2018-06-06" }, { "invoiceNumber": 3218, "amountInCents": 0, "invoiceDate": "2018-06-05" }, { "invoiceNumber": 3142, "amountInCents": 0, "invoiceDate": "2018-06-04" }, { "invoiceNumber": 3066, "amountInCents": 0, "invoiceDate": "2018-06-03" }, { "invoiceNumber": 2997, "amountInCents": 0, "invoiceDate": "2018-06-02" }, { "invoiceNumber": 2929, "amountInCents": 0, "invoiceDate": "2018-06-01" }, { "invoiceNumber": 2862, "amountInCents": 0, "invoiceDate": "2018-05-31" }, { "invoiceNumber": 2798, "amountInCents": 0, "invoiceDate": "2018-05-30" }, { "invoiceNumber": 2735, "amountInCents": 0, "invoiceDate": "2018-05-29" }, { "invoiceNumber": 2673, "amountInCents": 0, "invoiceDate": "2018-05-28" }, { "invoiceNumber": 2612, "amountInCents": 0, "invoiceDate": "2018-05-27" }, { "invoiceNumber": 2553, "amountInCents": 0, "invoiceDate": "2018-05-26" }, { "invoiceNumber": 2494, "amountInCents": 0, "invoiceDate": "2018-05-25" }, { "invoiceNumber": 2435, "amountInCents": 0, "invoiceDate": "2018-05-24" }, { "invoiceNumber": 2375, "amountInCents": 0, "invoiceDate": "2018-05-23" }, { "invoiceNumber": 2317, "amountInCents": 0, "invoiceDate": "2018-05-22" }, { "invoiceNumber": 2259, "amountInCents": 0, "invoiceDate": "2018-05-21" }, { "invoiceNumber": 2202, "amountInCents": 0, "invoiceDate": "2018-05-20" }, { "invoiceNumber": 2145, "amountInCents": 0, "invoiceDate": "2018-05-19" }, { "invoiceNumber": 2090, "amountInCents": 0, "invoiceDate": "2018-05-18" }, { "invoiceNumber": 2036, "amountInCents": 0, "invoiceDate": "2018-05-17" }, { "invoiceNumber": 1982, "amountInCents": 0, "invoiceDate": "2018-05-16" }, { "invoiceNumber": 1931, "amountInCents": 0, "invoiceDate": "2018-05-15" }, { "invoiceNumber": 1884, "amountInCents": 0, "invoiceDate": "2018-05-14" }, { "invoiceNumber": 1837, "amountInCents": 0, "invoiceDate": "2018-05-13" }, { "invoiceNumber": 1790, "amountInCents": 0, "invoiceDate": "2018-05-12" }, { "invoiceNumber": 1744, "amountInCents": 0, "invoiceDate": "2018-05-11" }, { "invoiceNumber": 1694, "amountInCents": 0, "invoiceDate": "2018-05-10" }, { "invoiceNumber": 1646, "amountInCents": 0, "invoiceDate": "2018-05-09" }, { "invoiceNumber": 1602, "amountInCents": 0, "invoiceDate": "2018-05-08" }, { "invoiceNumber": 1557, "amountInCents": 0, "invoiceDate": "2018-05-07" }, { "invoiceNumber": 1512, "amountInCents": 0, "invoiceDate": "2018-05-06" }, { "invoiceNumber": 1469, "amountInCents": 0, "invoiceDate": "2018-05-05" }, { "invoiceNumber": 1426, "amountInCents": 0, "invoiceDate": "2018-05-04" }, { "invoiceNumber": 1382, "amountInCents": 0, "invoiceDate": "2018-05-03" }, { "invoiceNumber": 1340, "amountInCents": 0, "invoiceDate": "2018-05-02" }, { "invoiceNumber": 1295, "amountInCents": 0, "invoiceDate": "2018-05-01" }, { "invoiceNumber": 1260, "amountInCents": 0, "invoiceDate": "2018-04-30" }, { "invoiceNumber": 1227, "amountInCents": 0, "invoiceDate": "2018-04-29" }, { "invoiceNumber": 1200, "amountInCents": 0, "invoiceDate": "2018-04-28" }, { "invoiceNumber": 1173, "amountInCents": 0, "invoiceDate": "2018-04-27" }, { "invoiceNumber": 1147, "amountInCents": 0, "invoiceDate": "2018-04-26" }] }
        // response.Payload = JSON.stringify(response.Payload)
        // onSuccess(response);
        LambdaUtil.invokeLambda(params, onSuccess, onError);
    }

    static downladInvoice(stage, invoiceNumber = null, onSuccess, onError) {

        var payload = null;
        if (invoiceNumber != null && invoiceNumber !== -1) {
            payload = '{"invoiceNumber": "' + invoiceNumber + '"}';
        }

        var params = {
            FunctionName: "lambda-kloud-" + stage + "-retrieveInvoice",
            InvocationType: "RequestResponse",
            LogType: "Tail",
            Payload: payload
        };
        LambdaUtil.invokeLambda(params, onSuccess, onError);
    }

    static inviteUser(stage, tagName, role, targetEmail, onSuccess, onError) {

        var payload = `{"tag_name":"${tagName}", "role": "${role}", "email": "${targetEmail}"}`;
        var params = {
            FunctionName: "lambda-kloud-" + stage + "-inviteUser",
            InvocationType: "RequestResponse",
            LogType: "Tail",
            Payload: payload
        };
        LambdaUtil.invokeLambda(params, onSuccess, onError);
    }

    static removeSharedRavenAccess(stage, email, onSuccess, onError) {

        var payload = '{"email": "' + email + '"}';
        
        var params = {
            FunctionName: "lambda-kloud-" + stage + "-removeRavenAccess",
            InvocationType: "RequestResponse",
            LogType: "Tail",
            Payload: payload
        };
        LambdaUtil.invokeLambda(params, onSuccess, onError);
    }

    static acceptRavenInvitation(stage, token, signature, onSuccess, onError) {

        var payload = '{ "token": "' + token + '", "signature": "' + signature + '", "acceptInvitation": true }';
        var params = {
            FunctionName: "lambda-kloud-" + stage + "-inviteRSVP",
            InvocationType: "RequestResponse",
            LogType: "Tail",
            Payload: payload,
        };

        LambdaUtil.invokeLambda(params, onSuccess, onError);
    }

    static declineRavenInvitation(stage, ravenUuid, onSuccess, onError) {

        var payload = '{"ravenUuid":"' + ravenUuid + '"}';
        var params = {
            FunctionName: "lambda-kloud-" + stage + "-declineInvitation",
            InvocationType: "RequestResponse",
            LogType: "Tail",
            Payload: payload
        };
        LambdaUtil.invokeLambda(params, onSuccess, onError);
    }

    static getReportingToken(stage) {
        var payload = '{}';

        var params = {
            FunctionName: "lambda-kloud-" + stage + "-generateReportingToken",
            InvocationType: "RequestResponse",
            LogType: "Tail",
            Payload: payload
        };
        return new Promise((resolve, reject) => {
            LambdaUtil.invokeLambda(params,
                data => resolve(data),
                error => reject(error)
            );
        });
    }

    static getSupportURL(stage, returnto, onSuccess, onError) {
        var payload = '{"return_to":"' + returnto + '"}';

        var params = {
            FunctionName: "lambda-kloud-" + stage + "-generateZendeskSSOUrl",
            InvocationType: "RequestResponse",
            LogType: "Tail",
            Payload: payload
        };
        LambdaUtil.invokeLambda(params, onSuccess, onError);
    }

    static getRavenCards(stage, ravenUuid, onSuccess, onError) {
        const query = deflate(`query {
            raven (ravenUuid:"${ravenUuid}") {
                cards {
                    cardId
                    segments { segId }
                    drivers {
                        name
                        startTime
                        image
                        identityId
                    }
                    hasPassengers
                    type
                    avgSpeed
                    maxSpeed
                    distance
                    thumbnailUrl
                    start{ lat lon ts locationName }
                    end { lat lon ts locationName }
                }
            }
        }`);
        LambdaUtil.invokeGraphQL(stage, JSON.stringify({query}), onSuccess, onError);
    }

    static getCard(stage, ravenUuid, cardId) {
        const query = deflate(`query {
            card (ravenUuid:"${ravenUuid}", cardId: "${cardId}") {
                cardId
                type
                thumbnailUrl
                vehicle: carPersona { name }
                start { lat lon ts locationName }
                end { lat lon ts locationName }
                route {lat lon ts }
                segments { segId type speedZones { maxSpeed speedLimit startTime endTime } }
                drivers {
                    name
                    startTime
                    image
                    identityId
                }
                hasPassengers
                media {
                    mediaId
                    ts
                    type
                    cameraType
                    url
                    status
                    statusTime
                    availableOnRaven
                }
                events {
                    type
                    ts
                    duration
                    description
                    videoAvailable {
                        cabin
                        road
                    }
                    level
                    media { mediaId }
                    mediaAutomatch
                    eventParams { key value }
                    geoStamp { ts lon lat }
                }
                geofences { 
                    geofenceId 
                    name 
                    style 
                    start 
                    end 
                    geojson 
                    created 
                    lastUpdated 
                }
            }
        }`);
        return LambdaUtil.invokeGraphQLForPromise(stage, JSON.stringify({query}));
    }

    // Used by driver event list thingy
    // Pass in an array of objects with ravenUuid and cardId as keys
    static getMediaIdsForCardEvents(stage, cardList) {
        var query = 'query {';

        var foundOne = false;

        cardList.forEach( (cardEntry) => {

            const ravenUuid = cardEntry['ravenUuid'];
            const cardId = cardEntry['cardId'];

            if(ravenUuid && cardId)
            {
                query += this.generateCardEventMediaQueryString(ravenUuid, cardId);
                foundOne = true;
            }

        });

        query += '}';

        if(foundOne)
        {
            return this.invokeGraphQLForPromise(stage, JSON.stringify({ query }));
        }
        else
        {
            return Promise.reject(Error('Nothing to query'));
        }
    }

    static generateCardEventMediaQueryString(ravenUuid, cardId)
    {
        return deflate(`
            card_${ravenUuid}_${cardId} : card (ravenUuid:"${ravenUuid}", cardId: "${cardId}") {
                cardId
                type
                eventList {
                    type
                    ts
                    duration
                    description
                    videoAvailable {
                        cabin
                        road
                    }
                    level
                    mediaList { 
                        mediaId 
                        ts
                        type
                        cameraType
                        url
                        status
                        statusTime
                        availableOnRaven
                    }
                    mediaAutomatch
                    eventParams { key value }
                    geoStamp { ts lon lat }
                }
            }
        `);
    }

    static getTripReport(stage, ravenUuid, cardId, unitOfMeasure, onSuccess, onError) {
        assertValidUnitOfMeasure(unitOfMeasure);
        var query = deflate(`query {
            tripReport (ravenUuid:"${ravenUuid}", cardId: "${cardId}", unitOfMeasure: "${unitOfMeasure}") {
                html
            }
        }`);
        LambdaUtil.invokeGraphQL(stage, JSON.stringify({query}), onSuccess, onError);
    }

    static getHeatMap(stage, start, end, onSuccess, onError) {
        var payload = '{ "query": "HEATMAP", "startTime": "' + moment(start).format('YYYY-MM-DD') + '", "endTime": "' + moment(end).format('YYYY-MM-DD') + '" }';
        LambdaUtil.invokeRavenStats(stage, payload, onSuccess, onError);

    }

    static lookupIdentities(stage, identityIds, withFaces) {
        let query = deflate(`query {
            lookupIdentities(identityIds: ${JSON.stringify(identityIds)}) {
                identities {
                    identityId
                    name
                    profileFace { faceId secureImageUrl }
                    ${ withFaces ? 'faces { faceId secureImageUrl detectedTime }' : '' }
                }
            }
        }`);
        return LambdaUtil.invokeGraphQLForPromise(stage, JSON.stringify({query}));
    }

    static getFaces(stage) {
        let query = deflate(`query {
            listIdentities {
                knownIdentities {
                    identityId
                    name
                    profileFace { faceId secureImageUrl detectedTime }
                }
                unknownIdentities {
                    identityId
                    profileFace { faceId secureImageUrl detectedTime }
                }
            }
        }`);
        return LambdaUtil.invokeGraphQLForPromise(stage, JSON.stringify({query}));
    }

    static getNamedIdentities(stage) {
        let query = deflate(`query {
            listIdentities {
                knownIdentities {
                    identityId
                    name
                    profileFace { faceId secureImageUrl }
                }
            }
        }`);
        return LambdaUtil.invokeGraphQLForPromise(stage, JSON.stringify({query}));
    }

    static setIdentityName(stage, identityId, name) {
        var payload = {
            identityId: identityId,
            name: name
        };
        var params = {
            FunctionName: "lambda-kloud-" + stage + "-setIdentityName",
            InvocationType: "RequestResponse",
            LogType: "Tail",
            Payload: JSON.stringify(payload)
        };
        return LambdaUtil.invokeLambdaForPromise(params);
    }

    static setIdentityProfileFace(stage, identityId, faceId) {
        const payload = {
            identityId,
            faceId,
        };
        const params = {
            FunctionName: "lambda-kloud-" + stage + "-setIdentityProfileFace",
            InvocationType: "RequestResponse",
            LogType: "Tail",
            Payload: JSON.stringify(payload)
        };
        return LambdaUtil.invokeLambdaForPromise(params);
    }

    static mergeIdentities(stage, identityIds) {
        if (!identityIds) return Promise.reject('identityIds not provided');
        if (!identityIds.length || identityIds.length < 2) return Promise.reject('identityIds must contain at least 2 items');
        const payload = {
            mainIdentityId: identityIds[0],
            identityIds: identityIds.slice(1)
        };
        const params = {
            FunctionName: "lambda-kloud-" + stage + "-mergeIdentities",
            InvocationType: "RequestResponse",
            LogType: "Tail",
            Payload: JSON.stringify(payload)
        };
        return LambdaUtil.invokeLambdaForPromise(params);
    }

    static deleteIdentityFaces(stage, faceIds) {
        const payload = {
            faceIds: faceIds,
        }
        const params = {
            FunctionName: "lambda-kloud-" + stage + "-deleteIdentityFaces",
            InvocationType: "RequestResponse",
            LogType: "Tail",
            Payload: JSON.stringify(payload)
        };
        return LambdaUtil.invokeLambdaForPromise(params);
    }

    static unassignIdentityFaces(stage, faceIds) {
        const payload = {
            faceIds: faceIds,
            moveToNewIdentity: true
        };
        const params = {
            FunctionName: "lambda-kloud-" + stage + "-deleteIdentityFaces",
            InvocationType: "RequestResponse",
            LogType: "Tail",
            Payload: JSON.stringify(payload)
        };
        return LambdaUtil.invokeLambdaForPromise(params);
    }

    static deleteIdentities(stage, identityIds) {
        const payload = {
            identityIds
        }
        const params = {
            FunctionName: "lambda-kloud-" + stage + "-deleteIdentities",
            InvocationType: "RequestResponse",
            LogType: "Tail",
            Payload: JSON.stringify(payload)
        };
        return LambdaUtil.invokeLambdaForPromise(params);
    }

    static assignDriver(stage, ravenUuid, cardId, identityId) {
        const payload = {
            ravenUuid,
            cardId,
            identityId,
        }
        const params = {
            FunctionName: "lambda-kloud-" + stage + "-assignDriver",
            InvocationType: "RequestResponse",
            LogType: "Tail",
            Payload: JSON.stringify(payload)
        };
        return LambdaUtil.invokeLambdaForPromise(params);
    }

    static setSecurityPhoneNumber (stage, phoneNumber) {
        const payload = {
            phoneNumber: phoneNumber
        }
        const params = {
            FunctionName: "lambda-kloud-" + stage + "-setSecurityPhoneNumber",
            InvocationType: "RequestResponse",
            LogType: "Tail",
            Payload: JSON.stringify(payload)
        };
        return LambdaUtil.invokeLambdaForPromise(params);
    }

    static dispatchMessage(stage, payload, onSuccess, onError) {

        var params = {
            FunctionName: "lambda-kloud-" + stage + "-dispatchMessage",
            InvocationType: "RequestResponse",
            LogType: "Tail",
            Payload: JSON.stringify(payload)
        };
        LambdaUtil.invokeLambda(params, onSuccess, onError);
    }

    static finalizeAccount(stage, onSuccess, onError) {

        var params = {
            FunctionName: "lambda-kloud-" + stage + "-finalizeFleetAccountCreation",
            InvocationType: "RequestResponse",
            LogType: "Tail",
        };
        LambdaUtil.invokeLambda(params, onSuccess, onError);
    }

    static setVehicleName(stage, ravenUuid, name) {
        const params = {
            FunctionName: "lambda-kloud-" + stage + "-setCarPersona",
            InvocationType: "RequestResponse",
            LogType: "Tail",
            Payload: JSON.stringify({
                ravenUuid,
                name
            })
        };
        return LambdaUtil.invokeLambdaForPromise(params, true);
    }

    static getSettings(stage) {
        const params = {
            FunctionName: "lambda-kloud-" + stage + "-getSettings",
            InvocationType: "RequestResponse",
            LogType: "Tail",
        };
        return LambdaUtil.invokeLambdaForPromise(maybeAddQualifierToParams(stage, params))
            .catch(err => {
                if (err.code === 'ResourceNotFoundException') {
                    return LambdaUtil.getAccountSettings(stage);
                }
            });
    }

    static updateSettings(stage, settings) {
        if(!stage) return Promise.reject(Error('stage is required'));
        if(!settings) return Promise.reject(Error('settings is required'));
        if(!(settings.user || settings.account)) return Promise.reject(Error('settings.user or settings.account is required'));
        const params = {
            FunctionName: "lambda-kloud-" + stage + "-updateSettings",
            InvocationType: "RequestResponse",
            LogType: "Tail",
            Payload: JSON.stringify(settings)
        };
        return LambdaUtil.invokeLambdaForPromise(params)
            .catch(err => {
                if (err.code === 'ResourceNotFoundException') {
                    return LambdaUtil.updateAccountSettings(stage, settings);
                }
            });
    }

    static getAccountSettings(stage) {
        const params = {
            FunctionName: "lambda-kloud-" + stage + "-getAccountSettings",
            InvocationType: "RequestResponse",
            LogType: "Tail",
        };
        return LambdaUtil.invokeLambdaForPromise(params).then(response => {
            if(response.payload) {
                return {
                    ...response,
                    payload: {
                        account: response.payload,
                        user: response.payload
                    }
                };
            }
            return response;
        });
    }

    static updateAccountSettings(stage, settings) {
        if(!stage) return Promise.reject(Error('stage is required'));
        if(!settings) return Promise.reject(Error('settings is required'));
        const payload = { settings: {} };
        if (settings.user && settings.user.general) {
            payload.settings.general = settings.user.general;
        }
        if (settings.account && settings.account.facialRecognition) {
            payload.settings.facialRecognition = settings.account.facialRecognition;
        }
        const params = {
            FunctionName: "lambda-kloud-" + stage + "-updateAccountSettings",
            InvocationType: "RequestResponse",
            LogType: "Tail",
            Payload: JSON.stringify(payload)
        };
        return LambdaUtil.invokeLambdaForPromise(params).then(response => {
            if(response.payload) {
                return {
                    ...response,
                    payload: {
                        account: response.payload,
                        user: response.payload
                    }
                };
            }
            return response;
        });
    }

    static createStreamingToken(stage, ravenUuid, startTimestamp, cameraType, eventTimestamp, onSuccess, onError) {
        var params = {
            FunctionName: "lambda-kloud-" + stage + "-createStreamingToken",
            InvocationType: "RequestResponse",
            LogType: "Tail",
            Payload: JSON.stringify({
                ravenUuid,
                startTimestamp,
                cameraType,
                eventTimestamp,
                supportedStreamers: ["agora","tokbox"]
            })
        };
        // Returns an object with apiKey, sessionId, and token
        return LambdaUtil.invokeLambda(params, onSuccess, onError);
    }

    static invokeGraphQLv2 = (stage, command) => {
        if (!stage) return Promise.reject(new TypeError('stage is required'));
        if (!stage) return Promise.reject(new TypeError('command is required'));
        const { query, mutation, variables } = command;
        const statement = query || mutation;
        if (!statement) {
            return Promise.reject(new TypeError(`command must have a "query" or "mutation" property. Argument provided=${JSON.stringify(command)}`));
        }
        const functionName = query ? `lambda-kloud-v2-app-${stage}-graphqlQuery`
            : `lambda-kloud-v2-app-${stage}-graphqlMutation`;
        const params = {
            FunctionName: functionName,
            InvocationType: "RequestResponse",
            LogType: "None",
            Payload: JSON.stringify({ statement, variables })
        };
        return new Promise(function(resolve, reject) {
            LambdaUtil.invokeLambda(params,
                /* success */ (response) => {
                    if(response.StatusCode !== 200 ) {
                        reject(new Error(`StatusCode: ${response.StatusCode}`));
                        return;
                    }
                    try {
                        let payload = JSON.parse(response.Payload);
                        if (!payload) {
                            return reject(new Error(`Payload could not be parsed: ${response.Payload}`));
                        }
                        if (payload.errors) {
                            return reject(new GraphQlError(payload.errors, { functionName, statement, variables }));
                        }
                        resolve(payload.data);
                    } catch (e) {
                        reject(e);
                        return;
                    }
                },
                /* failure */ (err) => {
                    reject(err instanceof Error ? err : new Error(err));
                }
            );
        });
    }

    static getActiveGeofences(stage) {
        const query = deflate(`query {
            activeGeofences {
                geofenceId
                name
                description
                style
                start
                end
                geojson
                notification {
                    enter
                    exit
                }
            }
        }`);
        return LambdaUtil.invokeGraphQLv2(stage, { query });
    }

    static addGeofence(stage, geofence) {
        const mutation = deflate(`mutation($data: GeofenceInput!) {
            addGeofence(geofenceData: $data) {
                geofence {
                    geofenceId
                    name
                    description
                    style
                    start
                    end
                    geojson
                    notification {
                        enter
                        exit
                    }
                }
            }
        }`);
        const variables = {
            data: geofence
        };
        return LambdaUtil.invokeGraphQLv2(stage, { mutation, variables });
    }

    static updateGeofence(stage, geofence) {
        const {
            geofenceId: id,
            ...data
        } = geofence;
        const mutation = deflate(`mutation($id: ID!, $data: GeofenceInput!) {
            updateGeofence(geofenceId: $id, geofenceData: $data) {
                geofence {
                    geofenceId
                    name
                    description
                    style
                    start
                    end
                    geojson
                    notification {
                        enter
                        exit
                    }
                }
            }
        }`);
        const variables = {
            id,
            data
        };
        return LambdaUtil.invokeGraphQLv2(stage, { mutation, variables });
    }

    static removeGeofence(stage, geofenceId) {
        const mutation = deflate(`mutation {
            removeGeofence(geofenceId: ${geofenceId}) {
                geofence { geofenceId }
            }
        }`);
        return LambdaUtil.invokeGraphQLv2(stage, { mutation });
    }

    static getTags(stage) {
        const query = deflate(`query {
            tags {
                tagId
                parentTagId
                type
                name
                style
                permissions
                ravens {
                    ravenUuid
                }
                users {
                    email
                    role: accessLevel
                }
            }
        }`);
        return this.invokeGraphQLForPromise(stage, JSON.stringify({ query }));
    }

    static removeTag(stage, tag) {

        const removeTagMutation = `mutation ($name: String!) {
            deleteTag(name: $name){
                result
            }
        }`;

        const mutation = deflate(removeTagMutation);

        const variables = {
            name: tag.name
        };

        return this.invokeGraphQLv2(stage, { mutation, variables });
    }

    static updateTag(stage, tag) {
        const variables = {};

        const updateTagMutation = TagsMutationUtil.generateUpdateTagMutation(tag, variables);

        const mutation = deflate(updateTagMutation);

        return this.invokeGraphQLv2(stage, { mutation, variables });
    }

    static addTag(stage, tag) {
        const variables = {};

        const addTagMutation = TagsMutationUtil.generateAddTagMutation(tag, variables);
        const mutation = deflate(addTagMutation);

        return this.invokeGraphQLv2(stage, { mutation, variables });
    }

    static addUserToTag(stage, tag, userId, roleId) {
        const variables = {
            name: tag.name,
            cognitoId: userId,
            roleId: roleId
        };

        const mutation = deflate(`
            mutation ($name: String!, $cognitoId: String!, $roleId: String!) { 
                addUserToTag(name: $name, cognitoId: $cognitoId, roleId: $roleId) {
                    tag { 
                        name 
                    } 
                } 
            }`);

        return this.invokeGraphQLv2(stage, { mutation, variables });
    }

    static removeUserFromTag(stage, tag, userId) {
        const variables = {
            name: tag.name,
            cognitoId: userId
        };

        const mutation = deflate(`
            mutation ($name: String!, $cognitoId: String!) { 
                removeUserFromTag(name: $name, cognitoId: $cognitoId) {
                    tag { 
                        name 
                    } 
                } 
            }`);

        return this.invokeGraphQLv2(stage, { mutation, variables });
    }

    static updateUserRoleForTag(stage, tag, userId, roleId) {
        const variables = {
            name: tag.name,
            cognitoId: userId,
            roleId: roleId
        };

        const mutation = deflate(`
            mutation ($name: String!, $cognitoId: String!, $roleId: String!) { 
                removeUserFromTag(name: $name, cognitoId: $cognitoId) { tag { name } }
                addUserToTag(name: $name, cognitoId: $cognitoId, roleId: $roleId) { tag { name } } 
            }`);

        return this.invokeGraphQLv2(stage, { mutation, variables });
    }

    static getUsers(stage) {
        const query = deflate(`query {
            users {
                userId
                email
                driverName
                tags {
                    tagId
                    role: accessLevel
                }
            }
        }`);
        return this.invokeGraphQLForPromise(stage, JSON.stringify({ query }));
    }
    
    static addRavenToTag(stage, tag, tagAncestors, ravenUuid) {
        const variables = {
            ravenUuid: ravenUuid
        };

        
        const tags = [tag, ...tagAncestors];
        const [mutations, mutationParameters] = TagsMutationUtil.generateAddRavenToTagsMutation(tags, variables);
        
        const mutation = `mutation ($ravenUuid: String!, ${mutationParameters}) {
            ${mutations}
        }
        `;

        return this.invokeGraphQLv2(stage, { mutation, variables });
    }

    static removeRavenFromTag(stage, tag, tagDescendants, ravenUuid) {
        const variables = {
            ravenUuid: ravenUuid
        };
 
        const tags = [tag, ...tagDescendants];
        const [mutations, mutationParameters] = TagsMutationUtil.generateRemoveRavenFromTagsMutation(tags, variables);
        
        const mutation = `mutation ($ravenUuid: String!, ${mutationParameters}) {
            ${mutations}
        }
        `;

        return this.invokeGraphQLv2(stage, { mutation, variables });
    }
}


class TagsMutationUtil {
  
    // addTag mutation
    static generateAddTagMutation(tag, variables) {
        const {users, ravens, ...rest} = tag;
        
        variables.tagData = rest;
        variables.tagName = tag.name;

        const addTagMutation = `addTag(tagData: $tagData) {
                tag {
                    tagId
                    parentTagId
                    type
                    name
                    style
                }
            }`;

        const parameters = ['$tagData: TagInput!'];

        const [addUsersToTagMutation, addUsersToTagParameters] = TagsMutationUtil.generateAddUsersToTagMutation(tag, variables);
        const [addRavensToTagMutation, addRavensToTagParameters] = TagsMutationUtil.generateAddRavensToTagMutation(tag, variables);

        // Adding $tagName parameter to the mutation when there are no users or ravens to add
        // causes the mutation to fail with error "Variable '$tagName' is never used."
        if(addUsersToTagParameters !== '' || addRavensToTagParameters !== '') {
            parameters.push('$tagName: String!');
        }

        if(addUsersToTagParameters !== '') {
            parameters.push(addUsersToTagParameters);
        }

        if(addRavensToTagParameters !== '') {
            parameters.push(addRavensToTagParameters);
        }

        return `mutation (${parameters.join(', ')}) {
            ${addTagMutation}
            ${addUsersToTagMutation}
            ${addRavensToTagMutation}
        }
        `;
    }

    // updateTag mutation
    static generateUpdateTagMutation(tag, variables) {
        const {users, ravens, originalName, ...rest} = tag;
        
        variables.tagData = rest;
        variables.tagName = originalName;

        const updateTagMutation = `updateTag(name: $tagName, tagData: $tagData) {
                    tag {
                        tagId
                        parentTagId
                        type
                        name
                        style
                    }
                }`;

        const parameters = ['$tagData: TagInput!', '$tagName: String!'];

        return `mutation (${parameters.join(', ')}) {
            ${updateTagMutation}
        }
        `;
    }

    // addUsersToTag mutation
    static generateAddUsersToTagMutation(tag, variables) {
        
        let mutation = '';
        let parameters = [];

        tag.users.toAdd.forEach((user, index) => {
            variables[`newUser${index + 1}Id`] = user.userId;
            variables[`newUser${index + 1}Role`] = user.role;

            parameters.push(`$newUser${index + 1}Id: String!, $newUser${index + 1}Role: String!`);

            mutation += `addUser${index + 1}: addUserToTag(name: $tagName, cognitoId: $newUser${index + 1}Id, roleId: $newUser${index + 1}Role) {
                    tag {
                        name
                    }
                }`;
        });

        return [mutation, parameters.join(', ')];
    }

    // EditUserRoleForTag mutation
    static generateReAddUsersToTagMutation(tag, variables) {
        
        let mutation = '';
        let parameters = [];

        tag.users.toUpdate.forEach((user, index) => {
            // Add user with new role
            variables[`renewUser${index + 1}Id`] = user.userId;
            variables[`renewUser${index + 1}Role`] = user.role;

            parameters.push(`$renewUser${index + 1}Id: String!, $renewUser${index + 1}Role: String!`);

            mutation += `renewUser${index + 1}: addUserToTag(name: $tagName, cognitoId: $renewUser${index + 1}Id, roleId: $renewUser${index + 1}Role) {
                    tag {
                        name
                    }
                }`;
        });

        return [mutation, parameters.join(', ')];
    }

    // removeUsersFromTag mutation
    static generateRemoveUsersFromTagMutation(tag, variables) {
        
        let mutation = '';
        let parameters = [];

        tag.users.toRemove.forEach((user, index) => {
            variables[`removeUser${index + 1}Id`] = user.userId;

            parameters.push(`$removeUser${index + 1}Id: String!`);

            mutation += `removeUser${index + 1}: removeUserFromTag(name: $tagName, cognitoId: $removeUser${index + 1}Id) {
                    tag {
                        name
                    }
                }`;
        });

        return [mutation, parameters.join(', ')];
    }

    // addRavensToTag mutation
    static generateAddRavensToTagMutation(tag, variables) {
        
        let mutation = '';
        let parameters = [];

        tag.ravens.toAdd.forEach((raven, index) => {
            variables[`newRaven${index + 1}Id`] = raven.ravenUuid;

            parameters.push(`$newRaven${index + 1}Id: String!`);

            mutation += `addRaven${index + 1}: addRavenToTag(name: $tagName, ravenUuid: $newRaven${index + 1}Id) {
                    tag {
                        name
                    }
                }`;
        });

        return [mutation, parameters.join(', ')];
    }

    // removeRavensFromTag mutation
    static generateRemoveRavensFromTagMutation(tag, variables) {
        
        let mutation = '';
        let parameters = [];

        tag.ravens.toRemove.forEach((raven, index) => {
            variables[`removeRaven${index + 1}Id`] = raven.ravenUuid;

            parameters.push(`$removeRaven${index + 1}Id: String!`);

            mutation += `removeRaven${index + 1}: removeRavenFromTag(name: $tagName, ravenUuid: $removeRaven${index + 1}Id) {
                    tag {
                        name
                    }
                }`;
        });

        return [mutation, parameters.join(', ')];
    }

    // add Raven To Tags mutation
    static generateAddRavenToTagsMutation(tags, variables) {
    
        let mutation = '';
        let parameters = [];

        tags.forEach((tag, index) => {
            variables[`tag${index + 1}Name`] = tag.name;

            parameters.push(`$tag${index + 1}Name: String!`);

            mutation += `addRaven${index + 1}: addRavenToTag(name: $tag${index+1}Name, ravenUuid: $ravenUuid) {
                    tag {
                        name
                    }
                }
                `;
        });

        return [mutation, parameters.join(', ')];
    }

    
    // add Raven To Tags mutation
    static generateRemoveRavenFromTagsMutation(tags, variables) {
    
        let mutation = '';
        let parameters = [];

        tags.forEach((tag, index) => {
            variables[`tag${index + 1}Name`] = tag.name;

            parameters.push(`$tag${index + 1}Name: String!`);

            mutation += `removeRaven${index + 1}: removeRavenFromTag(name: $tag${index+1}Name, ravenUuid: $ravenUuid) {
                    tag {
                        name
                    }
                }
                `;
        });

        return [mutation, parameters.join(', ')];
    }
  }


